/**
 * src/sync/adminApi.js — fetch wrapper for /api/admin/* endpoints.
 *
 * Unlike src/sync/api.js (X-Dev-Sync-Token for /api/sync/*), admin endpoints
 * REQUIRE a Firebase JWT with role=admin. The token is pulled fresh on every
 * call via `projectAuth.currentUser.getIdToken()` — Firebase caches it client-side
 * and auto-refreshes ~5 min before expiry, so the cost is a no-op when the token
 * is still valid.
 *
 * Errors:
 *   - 401: no current user / token rejected / SA not configured server-side
 *   - 403: user authenticated but role != admin
 *   - 500/other: bubbled up with .status and .body for the caller to inspect
 *
 * Why not unify with src/sync/api.js?
 *   sync vs admin have different auth models (dev-token vs JWT) and different
 *   failure semantics (sync failures go to outbox + retry; admin failures
 *   bubble straight to the UI as toast). Keeping them separate makes both
 *   simpler than a unified function with branching auth + branching retry.
 */

import { getApiBaseUrl } from '@/sync/baseUrl';
import { getFirebaseTokenOrThrow } from '@/sync/authToken';

const DEFAULT_TIMEOUT_MS = 10_000;

// Re-exported so callers importing from '@/sync/adminApi' keep working.
// Canonical implementation lives in baseUrl.js.
export { getApiBaseUrl };

// Translates getFirebaseTokenOrThrow() errors into adminApi's makeError(401) shape
// so `request()` surfaces them consistently with HTTP 401/403 responses.
async function getFirebaseToken() {
  try {
    return await getFirebaseTokenOrThrow();
  } catch (err) {
    throw makeError(401, err.message || String(err));
  }
}

function makeError(status, message, body = null) {
  const e = new Error(message);
  e.status = status;
  e.body = body;
  return e;
}

async function request(method, path, { query, body, timeoutMs } = {}) {
  const token = await getFirebaseToken();
  const baseUrl = getApiBaseUrl().replace(/\/+$/, '');
  const url = new URL(`${baseUrl}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }

  const controller = new AbortController();
  const t = setTimeout(
    () => controller.abort(new DOMException('Timeout', 'AbortError')),
    timeoutMs ?? DEFAULT_TIMEOUT_MS,
  );

  let res;
  try {
    res = await fetch(url.toString(), {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(t);
    if (err && err.name === 'AbortError') {
      throw makeError(0, `adminApi timeout after ${timeoutMs ?? DEFAULT_TIMEOUT_MS}ms`);
    }
    throw makeError(0, `adminApi network failure: ${err.message || err}`);
  } finally {
    clearTimeout(t);
  }

  let parsed = null;
  const text = await res.text().catch(() => '');
  if (text) { try { parsed = JSON.parse(text); } catch (_) { parsed = text; } }

  if (!res.ok) throw makeError(res.status, `${method} ${path} HTTP ${res.status}`, parsed);
  return parsed;
}

// ─── Reconciliation endpoints ───────────────────────────────────────────────

export function fetchReconciliationRollups({ days = 30 } = {}) {
  return request('GET', '/api/admin/reconciliation', { query: { days } });
}

export function fetchReconciliationRuns({ collection, limit = 20 } = {}) {
  return request('GET', '/api/admin/reconciliation/runs', { query: { collection, limit } });
}

export function fetchReconciliationRunDetail(runId, { diffLimit = 100 } = {}) {
  if (!runId) throw new Error('runId is required');
  return request('GET', `/api/admin/reconciliation/runs/${encodeURIComponent(runId)}`,
    { query: { diffLimit } });
}

export function triggerReconciliationRun({ collection, mode = 'delta' }) {
  if (!collection) throw new Error('collection is required');
  return request('POST', '/api/admin/reconciliation/runs', { query: { collection, mode } });
}

// ─── Admin jobs endpoints (Sek. 2) ──────────────────────────────────────────

export function submitJob({ type, collection, parameters }) {
  if (!type) throw new Error('type is required');
  if (!collection) throw new Error('collection is required');
  return request('POST', '/api/admin/jobs', { body: { type, collection, parameters } });
}

export function fetchJobs({ status, collection, limit = 50 } = {}) {
  return request('GET', '/api/admin/jobs', { query: { status, collection, limit } });
}

export function fetchJobDetail(jobId) {
  if (!jobId) throw new Error('jobId is required');
  return request('GET', `/api/admin/jobs/${encodeURIComponent(jobId)}`);
}

// ─── Realtime POC endpoints (Etap 7b) ───────────────────────────────────────

export function postRealtimeTestBroadcast({ message, payload } = {}) {
  return request('POST', '/api/admin/realtime/test-broadcast', { body: { message, payload } });
}

// ─── Auth / roles endpoints (Etap 9 Faza 0) ─────────────────────────────────

/**
 * GET /api/auth/me — the caller's profile + authoritative roles from Postgres.
 * Uses the same Bearer JWT as the admin endpoints (any signed-in user; the
 * middleware gates /api/auth with a JWT, no role check).
 * @returns {Promise<{uid:string, email:string, displayName:string|null,
 *   claims:{admin:boolean, konstruktor:boolean, sprzedawca:boolean, produkcja:boolean}}>}
 */
export function fetchMe() {
  return request('GET', '/api/auth/me');
}

/**
 * GET /api/v1/users — admin-only user list (id + email + roles). Used by the
 * Admin role panel to resolve a typed email to its uid before PATCHing roles.
 * @returns {Promise<{count:number, items:Array<{id:string, email:string, displayName:string}>}>}
 */
export function fetchUsers() {
  return request('GET', '/api/v1/users');
}

/**
 * PATCH /api/admin/users/{id}/roles — replace a user's role booleans in PG.
 * Replaces the addAdminRole Cloud Function. Returns 204 (no body).
 */
export function setUserRoles(uid, { admin = false, konstruktor = false, sprzedawca = false, produkcja = false } = {}) {
  if (!uid) throw new Error('uid is required');
  return request('PATCH', `/api/admin/users/${encodeURIComponent(uid)}/roles`,
    { body: { admin, konstruktor, sprzedawca, produkcja } });
}
